import React, { useState, useCallback } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { debounce } from 'lodash';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: () => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  debounceMs?: number;
  showClearButton?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  sx?: any;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Search...',
  size = 'small',
  fullWidth = true,
  debounceMs = 300,
  showClearButton = true,
  variant = 'outlined',
  sx,
}) => {
  const [localValue, setLocalValue] = useState(value);

  // Debounced onChange handler (debounce returns a function with unknown deps)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce((newValue: string) => {
      onChange(newValue);
      if (onSearch && !newValue) {
        onSearch();
      }
    }, debounceMs),
    [onChange, onSearch, debounceMs]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
    if (onSearch) {
      onSearch();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onSearch) {
      debouncedOnChange.cancel();
      onChange(localValue);
      onSearch();
    }
  };

  return (
    <Box sx={sx}>
      <TextField
        size={size}
        variant={variant}
        fullWidth={fullWidth}
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: showClearButton && localValue ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                edge="end"
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
};
